import React, { useState } from 'react';
import { FileText, CheckCircle } from 'lucide-react';
import type { User } from '@supabase/supabase-js';

import { supabase } from '../../../lib/supabase';
import { useStudentOnboardingStore } from '../../../store/studentOnboarding.store';
import { Button } from '../../../components/ui/Button';

interface EulaStepProps {
  onComplete: () => void;
}

export function EulaStep({ onComplete }: EulaStepProps) {
  const { setEulaAccepted } = useStudentOnboardingStore();
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  React.useEffect(() => {
    const loadUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
      }
    };
    loadUser();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!accepted) {
      setError('You must accept the terms and conditions to continue');
      return;
    }

    if (!user) {
      setError('User not found. Please sign in again.');
      return;
    }

    setSaving(true);

    try {
      // Get the latest EULA version for students (user type)
      let eulaVersion: string | null = null;
      
      const { data: versionData, error: versionError } = await supabase
        .rpc('get_latest_eula_version', { eula_type: 'user' });

      if (!versionError && versionData) {
        eulaVersion = versionData;
      }

      // Fallback: If no version from RPC, use default version "1.0"
      // This matches mobile app behavior when no remote EULA is available
      if (!eulaVersion) {
        eulaVersion = '1.0';
      }

      // Update user profile to mark EULA as accepted
      // Set both eula_accepted_at AND eula_version (mobile app requires both)
      const updateData = {
        eula_accepted_at: new Date().toISOString(),
        eula_version: eulaVersion,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', user.id);

      if (updateError) throw updateError;

      setEulaAccepted(true);
      onComplete();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save acceptance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
          <FileText size={32} className="text-text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Terms & Conditions</h2>
        <p className="text-[#A9A3A3] mt-2">Please review and accept our terms</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* EULA Content */}
        <div className="bg-[#363232] rounded-lg p-4 max-h-96 overflow-y-auto">
          <div className="prose prose-invert max-w-none">
            <h3 className="text-lg font-semibold text-[#EBFFEE] mb-3">End User License Agreement</h3>
            <p className="text-sm text-[#A9A3A3] mb-4">
              By using RollOps, you agree to the following terms and conditions:
            </p>
            <ul className="text-sm text-[#A9A3A3] space-y-2 list-disc list-inside">
              <li>You agree to use RollOps in accordance with all applicable laws and regulations</li>
              <li>You are responsible for maintaining the confidentiality of your account</li>
              <li>You agree not to share your account credentials with others</li>
              <li>You understand that RollOps may collect and use your data as described in our Privacy Policy</li>
              <li>You agree to receive communications from RollOps regarding your account and services</li>
            </ul>
            <div className="mt-4 flex flex-col gap-2">
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-rollops-accent hover:text-[#EBFFEE] underline text-sm"
              >
                Read full Terms of Service &rarr;
              </a>
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-rollops-accent hover:text-[#EBFFEE] underline text-sm"
              >
                Read full Privacy Policy &rarr;
              </a>
            </div>
          </div>
        </div>

        {/* Acceptance Checkbox */}
        <label className="flex items-start gap-3 cursor-pointer p-4 bg-[#363232] rounded-lg hover:bg-[#3A3636] transition-colors">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-[#504A4A] bg-[#504A4A] text-rollops-accent focus:ring-[#9B1421]"
          />
          <div className="flex-1">
            <p className="text-sm text-[#EBFFEE] font-medium">
              I have read and agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-rollops-accent hover:text-[#EBFFEE] underline">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-rollops-accent hover:text-[#EBFFEE] underline">Privacy Policy</a>
            </p>
            <p className="text-xs text-[#A9A3A3] mt-1">
              You must accept the terms to continue using RollOps
            </p>
          </div>
          {accepted && (
            <CheckCircle size={20} className="text-green-500 flex-shrink-0" />
          )}
        </label>

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <Button
          type="submit"
          disabled={saving || !accepted}
          variant="red"
          size="lg"
          className="w-full font-semibold"
        >
          {saving ? 'Saving...' : 'Accept & Continue'}
        </Button>
      </form>
    </div>
  );
}
